import * as Sentry from "@sentry/electron/renderer";

import { openDatabase, saveSnapshotDatabase } from "../Utils/db";

import { getScenePresets } from "../data/scenePresetsSeed";

import { sceneTemplateService } from "../infrastructure/Services/SceneTemplateService";

import {
  buildStudioFromSceneTemplate,
  extractFrameToMask,
} from "./scenes/sceneTemplateStudio";

import { MaskType } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Mask.interface";

import {
  clearPendingSceneDesign,
  consumeSceneDesignSkipped,
  readPendingSceneDesign,
} from "./scenes/pendingSceneDesign";

import { seedSceneThumbnails } from "./scenes/seedSceneThumbnails";

import type { ISceneDB } from "../interfaces/Scenes";

import type { IStudio } from "../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import { UserType } from "../interfaces/User";

import AdapterModel from "./Adapters/AdapterModel";

import GamerAdapterModel from "./Adapters/GamerAdapterModel";

import LocalRecordingAdapterModel from "./Adapters/LocalRecordingAdapterModel";

import RadioAdapterModel from "./Adapters/RadioAdapterModel";

import StreamerAdapterModel from "./Adapters/StreamerAdapterModel";

import VirtualClassesAdapterModel from "./Adapters/VirtualClassesAdapterModel";

import type Cameras from "./Devices/Cameras";

import type ScenesManager from "./ScenesManager";

import CurrentWorkspace from "./Workspace";

class SceneInitializer {
  private scenesManager: ScenesManager;

  private cameras: Cameras;

  constructor(scenesManager: ScenesManager, cameras: Cameras) {
    this.scenesManager = scenesManager;

    this.cameras = cameras;
  }

  async initialize() {
    const db = await openDatabase();

    const scenes = (await db?.scenes.toArray()) || [];

    const userType = await CurrentWorkspace.getType();

    // El diseño elegido en el picker (SceneDesigns) tiene PRIORIDAD: se aplica

    // aunque ya existan escenas (re-crea la escena viva + las 3 del diseño).

    // `buildChosenDesignScenes` consume (borra) la elección, así no re-aplica en

    // arranques posteriores. Sin esto, un workspace con escenas previas entraba

    // por `loadExistingScene` y el diseño elegido se ignoraba.

    const chosen = await this.buildChosenDesignScenes();

    if (chosen && chosen.length > 0) {
      await this.createDefaultScene(db, userType, chosen);

      return;
    }

    // "Omitir" en el picker: el usuario dijo explícitamente que no quiere

    // escenas de diseño. Se consume acá (una sola vez, aunque ya haya escenas y

    // la marca no aplique) para que el seed local NO se siembre en el primer

    // arranque y no quede colgada para arranques futuros.

    const skipped = consumeSceneDesignSkipped();

    if (scenes.length > 0) {
      await this.loadExistingScene(scenes);
    } else {
      await this.createDefaultScene(db, userType, null, skipped);
    }
  }

  private async loadExistingScene(scenes: ISceneDB[]) {
    // Migración idempotente a studio.mask antes de cargar (muta `scenes` + DB).

    await this.migrateDecorativeFramesToMask(scenes);

    const current = scenes.find((item) => item.active) ?? scenes[0];

    if (!current) return;

    const adapter = new AdapterModel(current.model);

    await this.scenesManager.setModel(adapter.getModel());
  }

  /**
   * Migración una-vez, idempotente: promueve el "marco" decorativo full-canvas
   * (el viejo `area-1` portador: overlay activo con elementos, sin sourceSlot y
   * sin fuente) a `studio.mask` (chrome a nivel-escena), soltando el área. Así
   * las escenas YA guardadas se alinean con el modelo nuevo sin re-aplicar el
   * template, y quedan editables desde "Editar como diseño".
   *
   * Seguro: idempotente (tras extraer, el marco ya no existe → el 2do arranque
   * no encuentra nada; además salta si ya hay `mask` composition), por-escena
   * con try/catch (un fallo no traba el boot), y sólo toca escenas con marco
   * detectado. Re-persiste cada escena migrada y dispara un snapshot al final.
   */

  private async migrateDecorativeFramesToMask(scenes: ISceneDB[]) {
    const db = await openDatabase();

    if (!db) return;

    let changed = false;

    for (const scene of scenes) {
      try {
        const model = scene.model;

        if (!model?.areas?.length) continue;

        // Ya migrada → nada que hacer (idempotente).

        if (model.mask?.type === MaskType.composition) continue;

        const { areas, mask } = extractFrameToMask(model.areas);

        if (!mask) continue; // no hay marco decorativo que promover

        const nextModel: IStudio = {
          ...model,

          areas,

          mask: model.mask ?? mask,
        };

        await db.scenes

          .where("id")

          .equals(scene.id)

          .modify((item) => {
            item.model = nextModel;
          });

        // Reflejar en memoria para el loadExistingScene posterior.

        scene.model = nextModel;

        changed = true;
      } catch (error) {
        console.error(
          "[SceneInitializer] migrateDecorativeFramesToMask failed for scene",

          scene.id,

          error,
        );
      }
    }

    if (changed) {
      try {
        await saveSnapshotDatabase();
      } catch (error) {
        console.error(
          "[SceneInitializer] snapshot after frame→mask migration failed",

          error,
        );
      }
    }
  }

  private async createDefaultScene(
    db: Awaited<ReturnType<typeof openDatabase>>,

    userType: UserType,

    chosen: IStudio[] | null,

    skippedDesign = false,
  ) {
    await db?.scenes.clear();

    // Diseño elegido en el picker (SceneDesigns): SOLO las 3 escenas del diseño

    // (Inicio / Pausa / Cierre), con la de Inicio (la primera = starting_soon)

    // activa. NO se crea la escena de cámara del adapter — entrás viendo el

    // diseño que elegiste. (Opción del usuario: a sabiendas de que no queda una

    // escena con la cámara hasta que cree una.)

    if (chosen && chosen.length > 0) {
      const designScenes = chosen.map(
        (model, index) =>
          ({
            model,

            order: index,

            active: index === 0,
          }) as ISceneDB,
      );

      await db?.scenes.bulkAdd(designScenes);

      await saveSnapshotDatabase();

      await this.scenesManager.setModel(chosen[0]);

      return;
    }

    // Sin diseño elegido (fallback local, o perfiles sin picker como Grabación

    // Local): escena viva del adapter (cámara) activa + presets locales.

    const adapter = this.createAdapter(userType);

    await adapter.setCameras(this.cameras);

    const liveScene: Omit<ISceneDB, "id"> = {
      model: adapter.getModel(),

      order: 0,

      active: true,
    };

    await db?.scenes.add(liveScene as ISceneDB);

    // Con "Omitir" el usuario arranca SOLO con la escena viva: sembrar igual

    // las gráficas locales le dejaba 4 escenas (3 vacías) después de haber

    // rechazado los diseños. El seed sigue vivo para quien nunca ve el picker

    // y para el fallback cuando el catálogo del backend no responde.

    const presets = skippedDesign ? [] : getScenePresets(userType);

    if (presets.length > 0) {
      const presetScenes = presets.map(
        (model, index) =>
          ({
            model,

            order: index + 1,

            active: false,
          }) as ISceneDB,
      );

      await db?.scenes.bulkAdd(presetScenes);
    }

    await saveSnapshotDatabase();

    await this.scenesManager.setModel(adapter.getModel());
  }

  /**
   * Si el usuario eligió un diseño en el picker (SceneDesigns), baja los
   * templates del backend, filtra los slugs elegidos y los materializa a
   * IStudio. Devuelve null si no eligió nada o si el backend no responde
   * (en ese caso el caller cae al seed local).
   *
   * Consumo NO destructivo: leemos los slugs sin borrar y sólo limpiamos la
   * elección si llegamos a una respuesta DEFINITIVA del backend (`list()`
   * resolvió). Si el backend está caído, conservamos el pending para reintentar
   * en el próximo arranque — si borráramos antes del fetch, una caída perdería
   * la elección del usuario para siempre.
   */

  private async buildChosenDesignScenes(): Promise<IStudio[] | null> {
    const slugs = readPendingSceneDesign();

    if (slugs.length === 0) return null;

    try {
      const all = await sceneTemplateService.list();

      // Respuesta definitiva del backend → consumir la elección (incluso si los

      // slugs ya no matchean ningún template, para no reintentar algo obsoleto).

      clearPendingSceneDesign();

      const bySlug = new Map(all.map((tpl) => [tpl.slug, tpl]));

      const chosen = slugs

        .map((slug) => bySlug.get(slug))

        .filter((tpl): tpl is NonNullable<typeof tpl> => Boolean(tpl));

      if (chosen.length === 0) return null;

      // map() pasaría el índice como 2º arg → lo envolvemos para que

      // buildStudioFromSceneTemplate resuelva el idioma activo por defecto.

      const studios = chosen.map((tpl) => buildStudioFromSceneTemplate(tpl));

      // Sembrar el thumbnail de cada escena en el path de screenshots

      // (${workspace}/scenes/<model.id>.jpg), así no salen rotas en la tira.

      // best-effort + awaited: los .jpg deben existir antes de que monte la

      // tira de escenas. `model.id` es único (scene-<slug>) → sin colisiones.

      await seedSceneThumbnails(
        studios.map((studio, i) => ({
          modelId: studio.id,

          imageUrl: chosen[i]?.imageUrl,
        })),
      );

      return studios;
    } catch (err) {
      // Backend no disponible → el caller usa el seed local. Antes se tragaba en

      // SILENCIO (el usuario perdía el diseño elegido sin ninguna traza y sin que

      // llegara nada a Sentry). Ahora lo reportamos como excepción.

      Sentry.captureException(err, {
        tags: { source: "buildChosenDesignScenes" },
      });

      return null;
    }
  }

  private createAdapter(userType: UserType) {
    switch (userType) {
      case UserType.Streamer:
        return new StreamerAdapterModel();

      case UserType.Gamer:
        return new GamerAdapterModel();

      case UserType.LocalRecording:
        return new LocalRecordingAdapterModel();

      case UserType.LiveConcerts:
        return new StreamerAdapterModel();

      case UserType.VirtualClasses:
        return new VirtualClassesAdapterModel();

      case UserType.Radio:

      // falls through

      default:
        return new RadioAdapterModel();
    }
  }
}

export default SceneInitializer;
